import React, { useState, useEffect, useRef } from 'react';
import CustomRefinementListContainer from './CustomRefinementListContainer';
import GoogleSortByContainer from './GoogleSortByContainer';
import GoogleHitsPerPage from './GoogleHitsPerPage';
import { renderGoogleFacet } from './renderGoogleFacet';
import { useGoogleSearchContext } from '../../../components/search/GoogleInstantSearch';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';

type Facet = { facet: string; type: string; title?: string };

interface GoogleRefinementContainerProps {
  facets: Array<Facet>;
  isSearchPage?: boolean;
  isDesktop?: boolean;
}

export const GoogleRefinementContainer = ({
  facets,
  isSearchPage = false,
  isDesktop = true,
}: GoogleRefinementContainerProps) => {
  const { state, nbHits } = useGoogleSearchContext();
  const [mobileShowFilter, setMobileShowFilter] = useState(false);
  const stickyEl = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);

  const activeFilterCount =
    state.facets.length +
    (state.isSaleItems ? 1 : 0) +
    (state.isClearanceItems ? 1 : 0) +
    (state.isExcludeMarketplace ? 1 : 0);

  const fixRefinement = () => {
    const refinementContainer = document.querySelector('.refinement-container') as HTMLElement;
    const refinementSlider = document.querySelector('#facets-container > div.slick-slider');
    const pageHeader = document.querySelector('#shopify-section-header') as HTMLElement;
    const seoTextB = document.querySelector('.seotext-b');
    const seoTextBPos = seoTextB ? seoTextB.getBoundingClientRect().top : '';
    const scrollTop = refinementContainer?.nextSibling
      ? (refinementContainer.nextSibling as HTMLElement).getBoundingClientRect().top -
        (stickyEl.current?.offsetHeight ?? 0)
      : 0;
    const searchRefinementContainer =
      isSearchPage && isDesktop ? (document.querySelector('.search-refinement-container div') as HTMLElement) : null;
    const searchResultsContainer = isSearchPage
      ? (document.querySelector('[data-search-results-content]') as HTMLElement)
      : null;
    const headerHeight = pageHeader?.offsetHeight || 0;

    if (
      scrollTop < (stickyEl.current?.offsetHeight ?? 0) &&
      ((stickyEl.current && seoTextBPos && seoTextBPos > (stickyEl.current?.offsetHeight ?? 0)) || !seoTextBPos)
    ) {
      if (!isSearchPage || !isDesktop) {
        refinementContainer.classList.add('scrollFix');
        refinementContainer.style.top = `${headerHeight}px`;
        refinementSlider?.classList.add('scrollFix');
      }
      if (searchRefinementContainer) {
        searchRefinementContainer.classList.add('refinementScrollFix');
        const stopAtEl = document.querySelector('.search-results-loop');
        let clampedTop = headerHeight;
        const searchRefinementHeight = searchRefinementContainer.getBoundingClientRect().height;
        if (stopAtEl) {
          const maxScrollPoint = stopAtEl.getBoundingClientRect().bottom;
          clampedTop =
            searchRefinementHeight + headerHeight > maxScrollPoint
              ? maxScrollPoint - searchRefinementHeight
              : headerHeight;
        }
        searchRefinementContainer.style.top = `${clampedTop}px`;
        if (searchResultsContainer) {
          searchResultsContainer.style.marginLeft = `${searchRefinementContainer.offsetWidth + 16}px`;
        }
      }
      setIsSticky(true);
    } else {
      refinementContainer?.classList.remove('scrollFix');
      if (refinementContainer) refinementContainer.style.top = '0';
      refinementSlider?.classList.remove('scrollFix');
      if (searchRefinementContainer) {
        searchRefinementContainer.classList.remove('refinementScrollFix');
        if (searchResultsContainer) {
          searchResultsContainer.style.marginLeft = '0';
        }
      }
      setIsSticky(false);
    }
  };

  const mobileResize = () => {
    if (window.innerWidth > 1010) {
      setMobileShowFilter(false);
    }
  };

  useEffect(() => {
    fixRefinement();

    const headerObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        const mutationElement = mutation.target as HTMLElement;
        if (mutationElement.classList.contains('headroom--pinned')) {
          fixRefinement();
        }
      });
    });

    const shopifyHeader = document.querySelector('#shopify-section-header');
    if (shopifyHeader) {
      headerObserver.observe(shopifyHeader, { attributes: true, attributeFilter: ['class'] });
    }
    window.addEventListener('JB-header-nav-pin-change', fixRefinement);
    window.addEventListener('scroll', fixRefinement);
    window.addEventListener('resize', mobileResize);
    return () => {
      headerObserver.disconnect();
      window.removeEventListener('JB-header-nav-pin-change', fixRefinement);
      window.removeEventListener('scroll', fixRefinement);
      window.removeEventListener('resize', mobileResize);
    };
  }, []);

  const handleFilterClick = () => {
    setMobileShowFilter(true);
    pushToDataLayer({ event: 'open_filters', search_type: 'site search' });
  };

  return (
    <>
      <div className="refinement-container">
        <div ref={stickyEl} className="sticky-filters">
          <button
            className={`refinement-mobile-filter ${activeFilterCount > 0 ? 'highlighted' : ''}`}
            onClick={handleFilterClick}
          >
            Filter {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
          </button>
          {!isSearchPage || !isDesktop ? (
            <CustomRefinementListContainer
              initialFacets={facets}
              nbHits={nbHits}
              priceRange={{ min: 0, max: 0 }}
              mobileShow={mobileShowFilter}
              setMobileShowFilter={setMobileShowFilter}
              trackingInfo={{ defaults: { hitsPerPage: 36, sortBy: 'relevancy' } }}
              isSearchPage={isSearchPage}
              isSticky={isSticky}
              nbSaleItems={0}
              nbClearanceItems={0}
              renderFacet={(f, t, i, s, o) => renderGoogleFacet(f, t, i, s, o, isSearchPage)}
            />
          ) : null}
          <GoogleHitsPerPage />
          <GoogleSortByContainer />
        </div>
      </div>
      <div
        className="refinement-filler"
        style={{ height: stickyEl.current ? `${stickyEl.current.offsetHeight}px` : 'initial' }}
      />
    </>
  );
};

export default GoogleRefinementContainer;
